import { defineStore } from "pinia";
import { Mimbox } from "./mimboxes";
import {
  getStatsWithResetFlag,
  OrderDirection,
  getLatestCaclulatedStats,
  Range,
  getCalculatedStatsRange,
  getStatsByIds,
  Int64String,
} from "@firebasegen/default-connector";
import {
  MB2WaterSaved,
  MB2WaterUsed,
  MB2RecyclingRate,
  MB2RecyclingFactor,
  MB2WMTotalConsumption,
  MB2StatsDiff,
  MB2NewRecyclingRate,
} from "@/mixins/calculations";
import { mb1AggregateLimiter } from "@/utils/concurrency";
import { db, functions } from "@/mixins/firebase";
import { numberToCode } from "@/mixins/common";
import { MB1Error } from "@/mixins/error_codes";
import { and, collection, getDocs, query, where, documentId } from "firebase/firestore";
import { unref } from "vue";
import { useAuthStore } from "./auth";
import { httpsCallable } from "firebase/functions";
import { getDaysPassed } from "@locale-utils";

export type RawStat = {
  id?: string | null;
  mmid?: string | null;
  saved?: number | null;
  savedNormalMode?: number | null;
  coldIn?: number | null;
  coldInNormalMode?: number | null;
  hotOut?: number | null;
  hotOutNormalMode?: number | null;
  greyTotOut?: number | null;
  greyTotOutNormalMode?: number | null;
  greyWm2Out?: number | null;
  greyWm2OutNormalMode?: number | null;
  rsrp?: number | null;
  errmask?: Int64String | null;
  runMode?: number | null;
  mainPump1Runtime?: number | null;
  mainPump2Runtime?: number | null;
  wcuPumpRuntime?: number | null;
  wasReset?: boolean | null;
  ts?: string | null;
};

export type Stat = {
  errmask?: number | null;
  waterSaved?: number | null;
  waterStored?: number | null;
  waterUsed?: number | null;
  recyclingRate?: number | null;
  newRecyclingRate?: number | null;
  recyclingFactor?: number | null;
  microplastics?: number;
  co2?: number;
  runMode?: number | number[] | null;
  firstSeenError?: number;
  updatedAt?: any;
  WMTotalConsumption?: number | null;
  tapWater?: number | null;
  rawStatId?: string | null;
};

type Id = string;
type CacheKey = string;
type Mmid = string;

export type Stats = Record<Id, Stat>;
type LatestStats = Record<Id, { stat: Stat; lastFetched: Date }>;
type StatsStoreState = {
  loading: boolean;
  latest: LatestStats;
  ttlLatest: number; // MS
  cachedInterval: Record<Id, Record<CacheKey, Stat>>;
  mb1IntervalPromiseCache: Record<string, Promise<Stat>>;
  getPromiseCache: Record<string, Promise<Stats>>;
  maxFromDate: Record<Mmid, Date | "NOLIMIT">;
  callId: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
  currentController: AbortController | null;
  statsCheckedAt: Date | null;
  currentFetchUUID: string | null;
};

export const useStatsStore = defineStore("stats", {
  state: (): StatsStoreState => {
    return {
      loading: false,
      latest: {},
      ttlLatest: 5 * 60 * 1000, // 5 minutes in ms,
      cachedInterval: {},
      mb1IntervalPromiseCache: {},
      getPromiseCache: {},
      maxFromDate: {},
      callId: 0,
      timeoutId: null,
      currentController: null,
      statsCheckedAt: null,
      currentFetchUUID: null,
    };
  },
  actions: {
    detachStatsFromState(stats: Stats): Stats {
      const unwrapped: Stats = {};
      Object.entries(stats).forEach(([key, stat]) => {
        unwrapped[key] = { ...unref(stat) };
      });
      return unwrapped;
    },
    getCacheLatest(mimbox: Mimbox) {
      if (!this.latest?.[mimbox.id]) return;
      const stat = this.latest[mimbox.id];
      const expired = Date.now() > stat.lastFetched.getTime() + this.ttlLatest;
      if (expired) {
        delete this.latest[mimbox.id];
        return;
      }
      return stat;
    },
    generateCacheKey(interval: { from: Date; to: Date }) {
      return `${interval.from.getTime() + interval.to.getTime()}`;
    },
    calculateMB2Stat(rawStat: RawStat): Stat {
      return {
        errmask: rawStat?.errmask != null ? Number(rawStat.errmask) : null,
        waterStored: rawStat?.savedNormalMode,
        waterSaved: MB2WaterSaved(rawStat),
        waterUsed: MB2WaterUsed(rawStat),
        recyclingRate: MB2RecyclingRate(rawStat),
        newRecyclingRate: MB2NewRecyclingRate(rawStat),
        recyclingFactor: MB2RecyclingFactor(rawStat),
        runMode: rawStat?.runMode ?? -1,
        updatedAt: rawStat?.ts ? new Date(rawStat.ts).getTime() : null,
        WMTotalConsumption: MB2WMTotalConsumption(rawStat),
        tapWater: rawStat?.coldInNormalMode,
      };
    },
    async calculateMB1Stat(
      mimbox: Mimbox,
      overwrites?: {
        waterSaved: number;
        waterUsed: number;
      },
    ): Promise<Stat> {
      const authStore = useAuthStore();
      let waterSaved = mimbox.water_saved ?? 0;
      let waterUsed = mimbox.water_used ?? 0;
      if (overwrites) {
        waterSaved = overwrites.waterSaved;
        waterUsed = overwrites.waterUsed;
      }
      const totalWater = waterSaved + waterUsed;
      const recyclingRate =
        totalWater > 0 ? Number(((waterSaved / totalWater) * 100).toFixed(2)) : 0;
      const recyclingFactor = waterUsed > 0 ? Number((totalWater / waterUsed).toFixed(2)) : 0;

      const rawErrorCodes = mimbox.admin_page?.last_error?.error_code ?? null;
      const parsedErrorCodes =
        !Number.isNaN(rawErrorCodes) && rawErrorCodes != null ? numberToCode(rawErrorCodes) : null;
      let firstSeenError = mimbox?.admin_page?.last_error?.timestamp ?? null;
      // If permanent MB1 error, use denormalized first seen timestamp (only for admins)
      if (
        parsedErrorCodes &&
        parsedErrorCodes.includes(MB1Error.PERMANENT_ERROR_STATE) &&
        authStore.is.staff()
      ) {
        firstSeenError = mimbox.admin_page?.permanent_error_first_seen_timestamp ?? firstSeenError;
      }
      return {
        waterSaved,
        waterUsed,
        recyclingRate,
        recyclingFactor,
        runMode: parsedErrorCodes,
        firstSeenError,
        updatedAt: mimbox.updatedAt,
        WMTotalConsumption: totalWater,
        tapWater: waterUsed,
      };
    },

    async getIntervalCache(mimbox: Mimbox, cacheKey: string) {
      const cached = this.cachedInterval[mimbox.id]?.[cacheKey];
      if (!cached) return null;
      const freshLatests = await this.getLatestStats([mimbox]);
      const latestStat = freshLatests?.[mimbox.id].stat || {};
      const newCache = {
        ...latestStat,
        ...cached,
      };

      return newCache;
    },
    /**
     * Hide stats for mimboxes that has hidePerformanceData = true and user is not admin by filter out those mimboxes
     */
    hideStats(mimboxes: Mimbox[]): Mimbox[] {
      const authStore = useAuthStore();
      const considerHidePerformance = !authStore.is.staff();

      if (!considerHidePerformance) return mimboxes;
      return mimboxes.filter((mimbox) => !mimbox.hidePerformanceData);
    },
    /**
     * Get Stats for selected interval
     */
    async getStatsWithinInterval(
      mimboxes: Mimbox[],
      interval: { from: Date; to: Date },
      range?: Range,
    ) {
      const cacheKey = this.generateCacheKey(interval);
      const intervalStats: Stats = {};

      mimboxes = this.hideStats(mimboxes);
      const from = interval.from;
      const to = interval.to;

      // Look in cache
      const cachedStats = await Promise.all(
        mimboxes.map(async (mimbox) => ({
          stat: await this.getIntervalCache(mimbox, cacheKey),
          id: mimbox.id,
        })),
      );
      // use cache result
      cachedStats.forEach(({ stat, id }) => {
        if (!stat) return;
        intervalStats[id] = stat;
      });
      // Find non cached boxes
      const nonCachedMimboxIds = cachedStats.filter(({ stat }) => !stat).map(({ id }) => id);
      const nonCachedMimboxes = mimboxes.filter((mimbox) => nonCachedMimboxIds.includes(mimbox.id));

      // Mb2
      let MB2s = nonCachedMimboxes.filter(({ mmid }) => !!mmid);
      if (range) {
        const { data } = await getCalculatedStatsRange({
          mmids: MB2s.map(({ mmid }) => mmid as string),
          range: range,
        });
        const preCalclulated = data.calculatedStatRanges;
        const foundMimboxIDs: string[] = [];
        preCalclulated.forEach((preCalc) => {
          const mimbox = MB2s.find(({ mmid }) => mmid === preCalc.mmid);
          if (!mimbox) return;
          if (!this.cachedInterval?.[mimbox.id]) {
            this.cachedInterval[mimbox.id] = {};
          }
          const updateAtParsed = new Date(preCalc.updatedAt);
          const daysPassed = getDaysPassed(updateAtParsed);
          // FIXME: This is a bad hack for now. Needs to be fixed
          if (daysPassed > 2) {
            return;
          }
          const updatedAt = updateAtParsed.getTime();
          const stat: Stat = {
            ...preCalc,
            updatedAt,
            errmask: preCalc.errmask != null ? Number(preCalc.errmask) : undefined,
          };
          this.cachedInterval[mimbox.id][cacheKey] = stat;
          intervalStats[mimbox.id] = stat;
          foundMimboxIDs.push(mimbox.id);
        });
        MB2s = MB2s.filter(({ id }) => !foundMimboxIDs.includes(id));
      }

      const latestStats = await this.getLatestStats(MB2s);

      const queryOnMmids = MB2s.map(({ mmid }) => mmid as string);

      const statIds = MB2s.map((mimbox) => latestStats?.[mimbox.id]?.stat.rawStatId).filter(
        (id) => !!id,
      );
      const { data: statsData } = await getStatsByIds({ ids: statIds as string[] });
      const bulkStats = statsData.stats;

      if (queryOnMmids.length > 0) {
        const getMB2IntervalStats = httpsCallable(functions, "getMB2IntervalStats");
        try {
          const res = await getMB2IntervalStats({
            mmids: queryOnMmids,
            from: from.toISOString(),
            to: to.toISOString(),
          });
          const fetchedStats: Record<string, any> = res.data as Record<string, any>;

          MB2s.forEach((mimbox) => {
            const mmid = mimbox.mmid as string;

            const latestStatRawStatId = latestStats?.[mimbox.id]?.stat.rawStatId;
            if (!latestStatRawStatId) {
              console.error("Unable to calculate interval for", mimbox.id, "Found no latest stat");
              return;
            }

            const latestStat = bulkStats.find((s) => s.id === latestStatRawStatId);
            if (!latestStat) {
              console.error("Unable to calculate interval for", mimbox.id, "Found no latest stat");
              return;
            }

            const fetched = fetchedStats[mmid] || { statInRange: null, statBeforeRange: null };
            const statInRange = fetched.statInRange;

            if (!statInRange) {
              const calculatedStatDiff = MB2StatsDiff(latestStat, latestStat);
              const buildStat = this.calculateMB2Stat({ ...latestStat, ...calculatedStatDiff });

              if (!this.cachedInterval?.[mimbox.id]) {
                this.cachedInterval[mimbox.id] = {};
              }
              this.cachedInterval[mimbox.id][cacheKey] = buildStat;
              intervalStats[mimbox.id] = buildStat;
              return;
            }

            const statBeforeRange = fetched.statBeforeRange || {};
            const calculatedStatDiff = MB2StatsDiff(statInRange, statBeforeRange as any);
            const buildStat = this.calculateMB2Stat({ ...latestStat, ...calculatedStatDiff });

            if (!this.cachedInterval?.[mimbox.id]) {
              this.cachedInterval[mimbox.id] = {};
            }
            this.cachedInterval[mimbox.id][cacheKey] = buildStat;
            intervalStats[mimbox.id] = buildStat;
          });
        } catch (error) {
          console.error("Failed to fetch batched MB2 interval stats:", error);
        }
      }
      // Mb1
      const MB1s = nonCachedMimboxes.filter(({ mmid }) => !mmid);
      if (MB1s.length > 0) {
        const formatHour = (d: Date) => {
          const year = d.getUTCFullYear();
          const month = String(d.getUTCMonth() + 1).padStart(2, "0");
          const day = String(d.getUTCDate()).padStart(2, "0");
          const hour = String(d.getUTCHours()).padStart(2, "0");
          return `${year}-${month}-${day}T${hour}`;
        };

        // By defaulting to the hour of 'from' and 'to', we include the edge hours.
        // This is much more accurate than strictly omitting them and losing hours of data.
        const startStr = formatHour(from);
        const endStr = formatHour(to);

        const totals: Record<string, { totalTap: number; totalRecycled: number }> = {};
        MB1s.forEach((m) => (totals[m.id] = { totalTap: 0, totalRecycled: 0 }));

        if (startStr <= endStr) {
          const q = query(
            collection(db, "mb1_hourly_stats"),
            and(where(documentId(), ">=", startStr), where(documentId(), "<=", endStr)),
          );
          const snapshot = await getDocs(q);
          snapshot.forEach((doc) => {
            const data = doc.data();
            MB1s.forEach((m) => {
              if (data[m.id]) {
                totals[m.id].totalTap += data[m.id].tap || 0;
                totals[m.id].totalRecycled += data[m.id].recycled || 0;
              }
            });
          });
        }

        for (const mimbox of MB1s) {
          const { totalTap, totalRecycled } = totals[mimbox.id];
          const buildStat = await this.calculateMB1Stat(mimbox, {
            waterSaved: totalRecycled,
            waterUsed: totalTap,
          });
          if (!this.cachedInterval?.[mimbox.id]) {
            this.cachedInterval[mimbox.id] = {};
          }
          this.cachedInterval[mimbox.id][cacheKey] = buildStat;
          intervalStats[mimbox.id] = buildStat;
        }
      }
      return intervalStats;
    },
    /**
     * Fetch latest stats
     */
    async getLatestStats(mimboxes: Mimbox[]): Promise<LatestStats> {
      let stats = this.latest; // Update this.latest with the requested boxes to always include as many boxes as possible
      const now = new Date();
      mimboxes = this.hideStats(mimboxes);
      // filter out any mimboxes that are already in cache
      mimboxes = mimboxes.filter((mimbox) => !this.getCacheLatest(mimbox));

      // get stats for MB2
      const MB2s = mimboxes.filter((mimbox) => !!mimbox?.mmid);
      const mmids = MB2s.map(({ mmid }) => mmid as string);

      if (mmids.length) {
        const { data } = await getLatestCaclulatedStats({ mmids });

        // First, default all requested MB2s to an empty cache object
        MB2s.forEach((mimbox) => {
          stats[mimbox.id] = {
            stat: { waterSaved: 0, waterUsed: 0, recyclingRate: 0 },
            lastFetched: now,
          };
        });

        // Then overwrite with real stats if they exist
        data.latestCalculatedStats.forEach((stat) => {
          const mimbox = MB2s.find(({ mmid }) => mmid === stat.mmid);

          if (!mimbox) return;
          const updatedAt = new Date(stat.updatedAt).getTime();
          stats[mimbox.id] = {
            stat: {
              ...stat,
              updatedAt,
              errmask: stat.errmask != null ? Number(stat.errmask) : undefined,
            },
            lastFetched: now,
          };
        });
      }

      // MB1
      const MB1s = mimboxes.filter((mimbox) => !mimbox?.mmid);
      await Promise.all(
        MB1s.map((mb1) =>
          mb1AggregateLimiter(async () => {
            stats[mb1.id] = { stat: await this.calculateMB1Stat(mb1), lastFetched: now };
          }),
        ),
      );
      this.latest = stats;
      return stats;
    },

    async internalGetFunc(
      mimboxes: Mimbox[],
      interval?: { from: Date; to: Date },
      range?: Range,
      ignoreLoadingState?: boolean,
    ) {
      const id = ++this.callId;
      if (!ignoreLoadingState) {
        this.loading = true;
      }
      if (interval) {
        const stats = await this.getStatsWithinInterval(mimboxes, interval, range);
        if (id === this.callId) {
          this.loading = false;
        }
        return this.detachStatsFromState(stats);
      }
      const stats = await this.getLatestStats(mimboxes);
      const convertedStats: Stats = {};
      Object.entries(stats).forEach(([key, obj]) => {
        convertedStats[key] = obj.stat;
      });
      if (id === this.callId) {
        this.loading = false;
      }

      return this.detachStatsFromState(convertedStats);
    },
    buildGetCallCacheKey(mimboxes: Mimbox[], interval?: { from: Date; to: Date }): string {
      return JSON.stringify({
        mimboxes: mimboxes.map((m) => ({
          id: m.id,
        })),
        interval: interval
          ? {
              from: interval.from.getTime(),
              to: interval.to.getTime(),
            }
          : null,
      });
    },
    /**
     * Get Stats for a selection of mimboxes
     * @param mimboxes A list of mimboxes to retrieve stats for
     * @param interval (Optional) a interval to fetch stats between
     * @param range (Optional) a predefined range that can possibly already have precalculated stat in DB (faster retreval)
     *
     * @returns `Stats` (`Record<Mimbox.id Stat>`)
     */
    async get(
      mimboxes: Mimbox[],
      interval?: { from: Date; to: Date },
      range?: Range,
      ignoreLoadingState?: boolean,
    ) {
      const getCacheKey = this.buildGetCallCacheKey(mimboxes, interval);
      if (getCacheKey in this.getPromiseCache) {
        return this.getPromiseCache[getCacheKey];
      }

      const fetchUUID = crypto.randomUUID();
      this.currentFetchUUID = fetchUUID;

      this.currentController?.abort();

      const controller = new AbortController();
      this.currentController = controller;

      const basePromise = this.internalGetFunc(mimboxes, interval, range, ignoreLoadingState);

      const promise = new Promise<Stats>((resolve, reject) => {
        basePromise
          .then((stats) => {
            if (this.currentFetchUUID !== fetchUUID) {
              reject(new Error("Request superseded"));
              return;
            }
            this.statsCheckedAt = new Date();
            setTimeout(() => {
              delete this.getPromiseCache[getCacheKey];
            }, 500);
            resolve(stats);
          })
          .catch((err) => {
            delete this.getPromiseCache[getCacheKey];
            reject(err);
          });
        controller.signal.addEventListener("abort", () => {
          delete this.getPromiseCache[getCacheKey];
          reject(new Error("Request aborted"));
        });
      });

      this.getPromiseCache[getCacheKey] = promise;
      return promise;
    },
    async validateInterval(
      mimbox: Mimbox,
      interval: { from: Date; to: Date },
    ): Promise<{ from: Date; to: Date } | undefined> {
      const maxFrom = await this.getMaxFromDate(mimbox.mmid);

      if (maxFrom === "NOLIMIT") return interval;

      const from = new Date(interval.from);
      const to = new Date(interval.to);
      const max = new Date(maxFrom);

      // Reject if interval ends before maxFrom
      if (to < max) {
        console.error("Interval was out of bounds for mmid", mimbox?.mmid);
        return undefined;
      }

      // Ensure from >= maxFrom
      return { from: from < max ? max : from, to };
    },
    /**
     * For MB2 we do not want to check Stats that is after the last reset.
     * This function will check for a reset for a MB2 and return that Date to be used to allow a MAX value for "from" time in queries.
     * If the box is not a MB2  or if no reset found it will return `"NOLIMIT"`. This should allow no restrictions on form date
     */
    async getMaxFromDate(mmid?: string): Promise<Date | "NOLIMIT"> {
      if (!mmid) return "NOLIMIT";

      if (this.maxFromDate?.[mmid]) {
        return this.maxFromDate[mmid];
      }
      const lastResetQuery = await getStatsWithResetFlag({
        mmid,
        sortOrder: OrderDirection.DESC,
        limit: 1,
        wasReset: true,
      });
      const lastResetStat = lastResetQuery.data.stats?.[0];
      if (!lastResetStat) {
        this.maxFromDate[mmid] = "NOLIMIT";
        return "NOLIMIT";
      }
      const maxFromDate = new Date(lastResetStat.ts);
      this.maxFromDate[mmid] = maxFromDate;

      return maxFromDate;
    },
  },
});
